<script setup lang="ts">
import { Form } from '@primevue/forms';
import type { FormSubmitEvent } from '@primevue/forms/form';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { FloatLabel, InputText, Message, Password, Select } from 'primevue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';
import passwordValidation from '@library/password-validation';

const { t } = useI18n();

const props = defineProps<{
    countryList?: Record<string, string> | undefined;
    disabled?: boolean;
    onFormSubmit?: (event: FormSubmitEvent) => void;
}>();

export type RegistrationDetailsModel = {
    firstName?: string;
    lastName?: string;
    company?: string;
    jobTitle?: string;
    country?: string;
    email?: string;
    howDidYouHearAboutUs?: string;
    howDidYouHearAboutUsOther?: string;
    password?: string;
    confirmPassword?: string;
};

const model = defineModel<RegistrationDetailsModel>({ default: {} });

const countries = computed(() =>
    Object.entries(props.countryList ?? {}).map(([code, name]) => ({
        code,
        name
    }))
);

const howDidYouHearAboutUsOptions = [
    { label: t('howDidYouHearAboutUs.searchEngine'), value: 'searchEngine' },
    { label: t('howDidYouHearAboutUs.linkedIn'), value: 'linkedIn' },
    { label: t('howDidYouHearAboutUs.otherSocialMedia'), value: 'otherSocialMedia' },
    { label: t('howDidYouHearAboutUs.emailFromUs'), value: 'emailFromUs' },
    { label: t('howDidYouHearAboutUs.friendOrColleague'), value: 'friendOrColleague' },
    { label: t('howDidYouHearAboutUs.onlineReviewOrArticle'), value: 'onlineReviewOrArticle' },
    { label: t('howDidYouHearAboutUs.advertisement'), value: 'advertisement' },
    { label: t('howDidYouHearAboutUs.existingCustomerClientReferral'), value: 'existingCustomerClientReferral' },
    { label: t('howDidYouHearAboutUs.eventOrWebinar'), value: 'eventOrWebinar' },
    { label: t('howDidYouHearAboutUs.other'), value: 'other' }
];

const resolver = zodResolver(
    z
        .object({
            company: z.string({ message: t('error.requiredField') }).min(1, t('error.requiredField')),
            confirmPassword: passwordValidation(t),
            country: z.string({ message: t('error.requiredField') }).min(1, t('error.requiredField')),
            email: z
                .string({ message: t('error.requiredField') })
                .trim()
                .nonempty({ message: t('error.requiredField') })
                .pipe(
                    z.string({ message: t('error.requiredField') }).and(
                        z.email({
                            message: t('error.invalidEmailAddress'),
                            pattern: z.regexes.unicodeEmail
                        })
                    )
                ),
            firstName: z.string({ message: t('error.requiredField') }).min(2, t('error.firstNameTooShort')),
            howDidYouHearAboutUs: z.string().optional(),
            howDidYouHearAboutUsOther: z.string().optional(),
            jobTitle: z.string({ message: t('error.requiredField') }).min(1, t('error.requiredField')),
            lastName: z.string({ message: t('error.requiredField') }).min(2, t('error.lastNameTooShort')),
            password: passwordValidation(t)
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: t('error.passwordsDoNotMatch'),
            path: ['confirmPassword']
        })
);
</script>

<template>
    <Form
        id="registration-details-form"
        v-slot="$form"
        class="flex flex-col gap-4"
        :resolver="resolver"
        :initialValues="model"
        @submit="onFormSubmit"
        :validateOnValueUpdate="false"
        :validateOnBlur="true">
        <div class="flex flex-col md:flex-row gap-4">
            <div class="flex-1 flex flex-col gap-1">
                <FloatLabel variant="on">
                    <InputText
                        v-model="model.firstName"
                        autocomplete="given-name"
                        :disabled="disabled"
                        autofocus
                        inputId="firstName"
                        name="firstName"
                        type="text"
                        fluid />
                    <label for="firstName">{{ $t('firstName') }}</label>
                </FloatLabel>
                <Message v-if="$form.firstName?.invalid" severity="error" size="small" variant="simple">{{
                    $form.firstName.error.message
                }}</Message>
            </div>
            <div class="flex-1 flex flex-col gap-1">
                <FloatLabel variant="on">
                    <InputText
                        v-model="model.lastName"
                        autocomplete="family-name"
                        :disabled="disabled"
                        inputId="lastName"
                        name="lastName"
                        type="text"
                        fluid />
                    <label for="lastName">{{ $t('lastName') }}</label>
                </FloatLabel>
                <Message v-if="$form.lastName?.invalid" severity="error" size="small" variant="simple">{{
                    $form.lastName.error.message
                }}</Message>
            </div>
        </div>
        <div class="flex flex-col md:flex-row gap-4">
            <div class="flex-1 flex flex-col gap-1">
                <FloatLabel variant="on">
                    <InputText
                        v-model="model.company"
                        autocomplete="organization"
                        :disabled="disabled"
                        inputId="company"
                        name="company"
                        type="text"
                        fluid />
                    <label for="company">{{ $t('company') }}</label>
                </FloatLabel>
                <Message v-if="$form.company?.invalid" severity="error" size="small" variant="simple">{{
                    $form.company.error.message
                }}</Message>
            </div>
            <div class="flex-1 flex flex-col gap-1">
                <FloatLabel variant="on">
                    <InputText
                        v-model="model.jobTitle"
                        autocomplete="organization-title"
                        :disabled="disabled"
                        inputId="jobTitle"
                        name="jobTitle"
                        type="text"
                        fluid />
                    <label for="jobTitle">{{ $t('jobTitle') }}</label>
                </FloatLabel>
                <Message v-if="$form.jobTitle?.invalid" severity="error" size="small" variant="simple">{{
                    $form.jobTitle.error.message
                }}</Message>
            </div>
        </div>
        <div class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <Select
                    v-model="model.country"
                    :disabled="disabled"
                    inputId="country"
                    name="country"
                    filter
                    :options="countries"
                    optionLabel="name"
                    optionValue="code"
                    showClear
                    fluid />
                <label for="country">{{ $t('country') }}</label>
            </FloatLabel>
            <Message v-if="$form.country?.invalid" severity="error" size="small" variant="simple">{{
                $form.country.error.message
            }}</Message>
        </div>

        <div class="flex flex-col gap-1 mt-4">
            <FloatLabel variant="on">
                <InputText
                    v-model="model.email"
                    autocomplete="off"
                    :disabled="disabled"
                    inputId="email"
                    name="email"
                    type="text"
                    fluid />
                <label for="email">{{ $t('email') }}</label>
            </FloatLabel>
            <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{
                $form.email.error.message
            }}</Message>
        </div>
        <div class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <Password
                    v-model="model.password"
                    inputId="password"
                    :disabled="disabled"
                    name="password"
                    :pt="{
                        pcInputText: { root: { autocomplete: 'new-password' } }
                    }"
                    :promptLabel="$t('chooseAPassword')"
                    :weakLabel="$t('tooWeak')"
                    :mediumLabel="$t('averageComplexity')"
                    :strongLabel="$t('complexPassword')"
                    fluid
                    toggleMask />
                <label for="password">{{ $t('password') }}</label>
            </FloatLabel>
            <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{
                $form.password.error.message
            }}</Message>
        </div>
        <div class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <Password
                    v-model="model.confirmPassword"
                    :feedback="false"
                    :disabled="disabled"
                    inputId="confirmPassword"
                    name="confirmPassword"
                    :pt="{
                        pcInputText: { root: { autocomplete: 'new-password' } }
                    }"
                    fluid
                    toggleMask />
                <label for="confirmPassword">{{ $t('confirmPassword') }}</label>
            </FloatLabel>
            <Message v-if="$form.confirmPassword?.invalid" severity="error" size="small" variant="simple">{{
                $form.confirmPassword.error.message
            }}</Message>
        </div>
        <div class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <Select
                    v-model="model.howDidYouHearAboutUs"
                    :disabled="disabled"
                    inputId="howDidYouHearAboutUs"
                    name="howDidYouHearAboutUs"
                    :options="howDidYouHearAboutUsOptions"
                    optionLabel="label"
                    optionValue="value"
                    showClear
                    fluid />
                <label for="howDidYouHearAboutUs">{{ $t('howDidYouHearAboutUs.label') }}</label>
            </FloatLabel>
        </div>
        <div v-if="model.howDidYouHearAboutUs === 'other'" class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <InputText
                    v-model="model.howDidYouHearAboutUsOther"
                    :disabled="disabled"
                    inputId="howDidYouHearAboutUsOther"
                    name="howDidYouHearAboutUsOther"
                    type="text"
                    fluid />
                <label for="howDidYouHearAboutUsOther">{{ $t('howDidYouHearAboutUs.otherPlaceholder') }}</label>
            </FloatLabel>
        </div>
    </Form>
</template>
